import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  getRecaptchaResponse,
  getRecaptchaSiteKey,
  renderRecaptcha,
  resetRecaptcha,
} from '@/lib/recaptcha';
import { verifySignupCaptcha } from '@/lib/verifySignupCaptcha';

interface AuthScreenProps {
  error: string | null;
  onClearError: () => void;
  onSignIn: (email: string, password: string) => Promise<boolean>;
  onSignUp: (name: string, email: string, password: string) => Promise<boolean>;
  onGoogle: () => Promise<boolean>;
}

export default function AuthScreen({
  error,
  onClearError,
  onSignIn,
  onSignUp,
  onGoogle,
}: AuthScreenProps) {
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState('');
  const captchaHostRef = useRef<HTMLDivElement | null>(null);
  const captchaWidgetIdRef = useRef<number | null>(null);

  useEffect(() => {
    if (mode !== 'signup') {
      captchaWidgetIdRef.current = null;
      setCaptchaToken('');
      return;
    }

    const siteKey = getRecaptchaSiteKey();
    if (!siteKey) {
      setLocalError(
        import.meta.env.PROD
          ? 'reCAPTCHA is not configured for this build. Add the GitHub secret VITE_RECAPTCHA_SITE_KEY and redeploy the web app.'
          : 'reCAPTCHA is not configured. Set VITE_RECAPTCHA_SITE_KEY in apps/web/.env.local.'
      );
      return;
    }

    let cancelled = false;
    const host = captchaHostRef.current;
    if (!host) return;

    void (async () => {
      try {
        const widgetId = await renderRecaptcha(host, {
          onChange: (token) => {
            if (!cancelled) setCaptchaToken(token);
          },
          onExpired: () => {
            if (!cancelled) setCaptchaToken('');
          },
          onError: () => {
            if (!cancelled) {
              setCaptchaToken('');
              setLocalError('CAPTCHA failed to load. Refresh and try again.');
            }
          },
        });
        if (cancelled) return;
        captchaWidgetIdRef.current = widgetId;
        setLocalError(null);
      } catch (err) {
        if (!cancelled) {
          setLocalError(
            err instanceof Error ? err.message : 'Failed to load CAPTCHA.'
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [mode]);

  const displayError = localError || error;

  const switchMode = (next: 'signin' | 'signup') => {
    setMode(next);
    onClearError();
    setLocalError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    onClearError();
    setLocalError(null);
    setBusy(true);
    try {
      if (mode === 'signin') {
        await onSignIn(email, password);
        return;
      }

      const token =
        captchaToken || getRecaptchaResponse(captchaWidgetIdRef.current);
      if (!token) {
        setLocalError('Complete the CAPTCHA before creating an account.');
        return;
      }

      try {
        await verifySignupCaptcha(token);
      } catch (err) {
        setLocalError(
          err instanceof Error ? err.message : 'CAPTCHA verification failed.'
        );
        resetRecaptcha(captchaWidgetIdRef.current);
        setCaptchaToken('');
        return;
      }

      const ok = await onSignUp(name, email, password);
      if (!ok) {
        resetRecaptcha(captchaWidgetIdRef.current);
        setCaptchaToken('');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    onClearError();
    setLocalError(null);
    setBusy(true);
    try {
      await onGoogle();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen">
      <section className="auth-hero">
        <div>
          <div className="brand">
            <img
              className="brand-mark"
              src="/brand/icon.png"
              alt=""
              width={36}
              height={36}
            />
            <span className="brand-text">
              Craftara <span>Design Studio</span>
            </span>
          </div>
          <h1>Design for class, career, and everything in between</h1>
          <p>
            Create posters, slides, social assets, and professional visuals in one
            focused canvas editor — built for students and working professionals.
          </p>
          <ul className="auth-points">
            <li>
              <div>
                <strong>Start from the right size</strong>
                Presentation slides, academic posters, LinkedIn banners, and more.
              </div>
            </li>
            <li>
              <div>
                <strong>Save to the cloud</strong>
                Projects sync to Firebase so you can pick up work on any device.
              </div>
            </li>
            <li>
              <div>
                <strong>Export when you are ready</strong>
                Download PNG or JPG for submissions, portfolios, and campaigns.
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section className="auth-card">
        <div className="auth-phone-brand">
          <img
            className="brand-mark"
            src="/brand/icon.png"
            alt=""
            width={36}
            height={36}
          />
          <span className="brand-text">
            Craftara <span>Design Studio</span>
          </span>
        </div>

        <div className="auth-tabs">
          <button
            type="button"
            className={mode === 'signin' ? 'active' : ''}
            onClick={() => switchMode('signin')}
          >
            Sign in
          </button>
          <button
            type="button"
            className={mode === 'signup' ? 'active' : ''}
            onClick={() => switchMode('signup')}
          >
            Create account
          </button>
        </div>

        <h2>{mode === 'signin' ? 'Welcome back' : 'Create account'}</h2>
        <p className="subtitle">
          {mode === 'signin'
            ? 'Sign in to open your projects and continue designing.'
            : 'Create a free account with email or Google. We will email a confirmation link.'}
        </p>

        {displayError && <div className="error-banner">{displayError}</div>}

        <form className="auth-form" onSubmit={(e) => void submit(e)}>
          {mode === 'signup' && (
            <div className="field">
              <label htmlFor="name">Name</label>
              <input
                id="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Alex Rivera"
                required
              />
            </div>
          )}

          <div className="field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@school.edu"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              type="password"
              autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              minLength={6}
              required
            />
          </div>

          {mode === 'signup' && (
            <div className="field captcha-field">
              <label>Confirm you are human</label>
              <div ref={captchaHostRef} className="recaptcha-host" />
            </div>
          )}

          <button className="btn btn-primary btn-block auth-email-submit" type="submit" disabled={busy}>
            {busy ? 'Please wait…' : mode === 'signin' ? 'Sign in' : 'Create account'}
          </button>
        </form>

        <div className="auth-divider">
          <span className="auth-divider-desktop">or</span>
          <span className="auth-divider-phone">or email</span>
        </div>

        <button
          className="btn btn-secondary btn-block auth-google"
          type="button"
          onClick={() => void handleGoogle()}
          disabled={busy}
        >
          Continue with Google
        </button>

        <p className="auth-switch">
          {mode === 'signin' ? (
            <>
              New here?{' '}
              <button type="button" onClick={() => switchMode('signup')}>
                Create account
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button type="button" onClick={() => switchMode('signin')}>
                Sign in
              </button>
            </>
          )}
        </p>
      </section>
    </div>
  );
}
